// ============================================================================
// MARK NO-SHOW CONFIRM DIALOG
// ============================================================================
// Confirms transition CONFIRMED → NO_SHOW with a mandatory free-text reason.
// Per CRS-Feature-Specification §4.12 — used when guest did not arrive by the
// check-in date and the front desk wants to release the room hold.

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { checkInService } from 'api/services/checkInService';
import { auditCorrectionService } from 'api/services/auditCorrectionService';
import type { Booking } from 'api/types/booking.types';
import notificationService from 'services/notificationService';

interface MarkNoShowConfirmDialogProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onMarked: () => void;
  /** Audit Corrections: the arrival date is closed — POST /AuditCorrections/{id}/no-show (reason up to 180). */
  auditCorrection?: boolean;
}

const MarkNoShowConfirmDialog: React.FC<MarkNoShowConfirmDialogProps> = ({
  isOpen, booking, onClose, onMarked, auditCorrection = false,
}) => {
  const maxReason = auditCorrection ? 180 : 500;
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setTouched(false);
    }
  }, [isOpen]);

  if (!isOpen || !booking) return null;

  const trimmedReason = reason.trim();
  const reasonError =
    trimmedReason.length < 3
      ? 'Reason is required (at least 3 characters).'
      : trimmedReason.length > maxReason
        ? `Reason must be ${maxReason} characters or fewer.`
        : '';

  const handleConfirm = async () => {
    setTouched(true);
    if (reasonError) return;
    setSubmitting(true);
    try {
      if (auditCorrection) {
        const result = await auditCorrectionService.markNoShow(booking.bookingId, trimmedReason);
        notificationService.toast.success(result.message);
      } else {
        await checkInService.markNoShow(booking.bookingId, { reason: trimmedReason });
        notificationService.toast.success(`Booking "${booking.bookingNumber}" marked as No-Show.`);
      }
      onMarked();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]
        || err?.message
        || 'Failed to mark No-Show.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={submitting ? undefined : onClose} />
      <div className="modal-container modal-sm">
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#ef4444', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Updating…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Mark Booking as No-Show</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.75rem 0.875rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            marginBottom: '0.875rem',
            fontSize: '0.85rem',
          }}>
            <div><strong>Booking:</strong> {booking.bookingNumber}</div>
            <div><strong>Guest:</strong> {booking.guestFullName || '—'}</div>
            <div><strong>Check-In Date:</strong> {booking.checkInDate?.slice(0, 10)}</div>
            <div style={{
              marginTop: '0.5rem',
              padding: '0.5rem 0.625rem',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '4px',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
            }}>
              Marking as No-Show releases the room hold and transitions the booking
              to <strong>NO_SHOW</strong>. This action is recorded in the audit log
              and cannot be reversed.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-textarea${touched && reasonError ? ' form-field-error' : ''}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Did not arrive by check-in date; phone unreachable."
              style={{ resize: 'vertical', minHeight: '70px' }}
              disabled={submitting}
            />
            {touched && reasonError && <div className="form-error-message">{reasonError}</div>}
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {trimmedReason.length}/500 characters
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={handleConfirm}
            disabled={submitting || !!reasonError}
          >
            Mark as No-Show
          </button>
        </div>
      </div>
    </>
  );
};

export default MarkNoShowConfirmDialog;
